/**
 * The emote picker (G): still poses, moving poses, dances and emoji. Each pose is shown on the player's own
 * figure; picking one sends the command and closes the picker.
 */
import {
  EMOJI_LIST,
  EMOJI_SECONDS,
  EMOTE_LIST,
  type EmoteDef,
  type EmoteKind,
  type PlayerCommand,
  type World,
} from '@nine/shared';
import { EMOTE_ART, emojiIcon } from '../render/emotes';
import { artX, artY, resOf } from '../render/pixel';
import type { SpriteBank } from '../render/sprites';
import { $, closeButton, h } from './dom';

type Tab = EmoteKind | 'dance' | 'emoji';
const TABS: [Tab, string][] = [
  ['still', 'ท่านิ่ง'],
  ['loop', 'ท่าเคลื่อนไหว'],
  ['dance', 'ท่าเต้น'],
  ['emoji', 'อีโมจิ'],
];
/** The emotes listed under a tab: dances have their own, apart from the other moving poses. */
const inTab = (tab: Tab, e: EmoteDef): boolean => (tab === 'dance' ? !!e.dance : e.kind === tab && !e.dance);

/** An icon drawn larger in whole pixels. */
function bigIcon(src: HTMLCanvasElement, scale: number): HTMLCanvasElement {
  const c = h('canvas', { class: 'px' });
  c.width = src.width * scale;
  c.height = src.height * scale;
  const g = c.getContext('2d')!;
  g.imageSmoothingEnabled = false;
  g.drawImage(src, 0, 0, c.width, c.height);
  return c;
}

export class EmoteMenu {
  readonly root = h('div', { id: 'emoteMenu', hidden: true, role: 'dialog', ariaLabel: 'ท่าโพสและอีโมจิ' });
  private readonly button = h('button', { type: 'button', id: 'emoteBtn', title: 'ท่าโพสและอีโมจิ (G)' });
  private tab: Tab = 'still';
  /** Pose thumbnails per look, with the frame each was drawn from. */
  private readonly thumbs = new Map<string, { from: HTMLCanvasElement; c: HTMLCanvasElement }>();
  private thumbLook = '';

  constructor(
    private readonly world: World,
    private readonly sprites: SpriteBank,
    private readonly send: (c: PlayerCommand) => void,
  ) {
    this.button.append(
      h('i', {}, bigIcon(emojiIcon('smile'), 2)),
      h('span', {}, 'ท่าโพสและอีโมจิ'),
      h('kbd', {}, 'G'),
    );
    this.button.addEventListener('pointerdown', (e) => {
      e.stopPropagation();
      this.toggle();
    });
    this.root.addEventListener('pointerdown', (e) => e.stopPropagation());
    // The button sits in the HUD's menu (เมนู Esc); the picker opens over the world.
    $('#gmPlay').append(this.button);
    $('#hud').append(this.root);
  }

  get isOpen(): boolean {
    return !this.root.hidden;
  }

  toggle(): void {
    this.root.hidden = !this.root.hidden;
    if (this.isOpen) this.show();
  }

  /** Renders the picker, then again as poses not drawn yet arrive (they are drawn between frames). */
  private show(): void {
    this.render();
    const tab = this.tab;
    for (const ms of [150, 500, 1200])
      setTimeout(() => this.isOpen && this.tab === tab && tab !== 'emoji' && this.render(), ms);
  }

  close(): void {
    this.root.hidden = true;
  }

  /** The player's figure in an emote's first pose, cropped to the body. */
  private thumb(id: string): HTMLCanvasElement | null {
    const p = this.world.player;
    const look = `${p.sprite}|${p.outfit.join(',')}`;
    if (look !== this.thumbLook) {
      this.thumbs.clear();
      this.thumbLook = look;
    }
    const art = EMOTE_ART[id];
    const set = this.sprites.of({ sprite: p.sprite, outfit: [...p.outfit, 'sheathed'] });
    const armedSet = this.sprites.of(p);
    const frame = art?.frames[0];
    const pick = id === 'guard' || id === 'kata' ? armedSet : set;
    if (!frame || !pick?.extra) return null;
    // A frame not drawn yet comes back as a stand-in: the thumbnail is redrawn once it is ready.
    const img = pick.extra(frame[0], frame[1]);
    const had = this.thumbs.get(id);
    if (had?.from === img) return had.c;
    const r = resOf(img);
    // The body around the feet anchor: wide enough for a sword held out.
    const w = 56;
    const hgt = 76;
    const c = h('canvas', { class: 'px' });
    c.width = w * r;
    c.height = hgt * r;
    const g = c.getContext('2d')!;
    g.imageSmoothingEnabled = false;
    // The frame placed so the feet anchor lands at the bottom middle; the canvas clips the rest.
    g.drawImage(img, (artX(img) - (pick.ax - w / 2)) * r, (artY(img) - (pick.ay - hgt + 2)) * r);
    this.thumbs.set(id, { from: img, c });
    return c;
  }

  private pick(cmd: PlayerCommand): void {
    this.send(cmd);
    this.close();
  }

  render(): void {
    const p = this.world.player;
    const tabs = h(
      'div',
      { class: 'em-tabs', role: 'tablist' },
      ...TABS.map(([t, label]) => {
        const b = h('button', { type: 'button', class: t === this.tab ? 'on' : '', role: 'tab' }, label);
        b.setAttribute('aria-selected', String(t === this.tab));
        b.addEventListener('click', () => {
          this.tab = t;
          this.show();
        });
        return b;
      }),
    );
    const grid = h('div', { class: `em-grid ${this.tab === 'emoji' ? 'emoji' : ''}` });
    if (this.tab === 'emoji')
      for (const e of EMOJI_LIST) {
        const b = h(
          'button',
          { type: 'button', title: e.name },
          bigIcon(emojiIcon(e.id), 2),
          h('small', {}, e.name),
        );
        b.addEventListener('click', () => this.pick({ t: 'emoji', id: e.id }));
        grid.append(b);
      }
    else
      for (const e of EMOTE_LIST.filter((x) => inTab(this.tab, x))) {
        const b = h(
          'button',
          { type: 'button', class: p.emote === e.id ? 'on' : '', title: e.name },
          this.thumb(e.id) ?? h('span'),
          h('small', {}, e.name),
        );
        b.addEventListener('click', () => this.pick({ t: 'emote', id: p.emote === e.id ? null : e.id }));
        grid.append(b);
      }
    const stop = h('button', { type: 'button', class: 'btn small ghost', disabled: !p.emote }, 'หยุดท่า');
    stop.addEventListener('click', () => this.pick({ t: 'emote', id: null }));
    this.root.replaceChildren(
      h(
        'header',
        {},
        h('b', {}, 'ท่าโพส · อีโมจิ'),
        stop,
        closeButton(() => this.close()),
      ),
      tabs,
      grid,
      h(
        'small',
        { class: 'em-hint' },
        this.tab === 'emoji'
          ? `อีโมจิลอยเหนือศีรษะ ${EMOJI_SECONDS} วินาที`
          : 'ยืนนิ่งก่อนจึงโพสท่าได้ · เดิน โจมตี หรือโดนตีจะหยุดท่า',
      ),
    );
  }
}
